import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
    BuildingStorefrontIcon,
    CalendarDaysIcon,
    CheckCircleIcon,
    ClockIcon,
    TagIcon
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { DataTable, useDataTable, type DataTableFilter, type DataTableRefs } from '@odo/shared-ui';
import { usePracticeOptions, useProducts } from '../../hooks/useBilling';
import { useMyPermissions } from '../../hooks/useMyPermissions';
import { useViewportPageSize } from '../../hooks/useViewportPageSize';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import { usePageTitle } from '../../hooks/usePageTitle';
import { DiscountRuleModal } from '../../components/billing/DiscountRuleModal';
import { StatCard } from '../../components/billing/StatusCards';
import {
    buildRows,
    compareRows,
    ENDING_SOON_DAYS,
    STATUS_LABEL,
    type DiscountsSortBy,
    type RuleRow
} from '../../components/billing/discounts/discountRows';
import { buildDiscountColumns } from '../../components/billing/discounts/discountColumns';
import { fetchDiscountCoverage } from '../../services/billing';

// Discounts - the global rule registry (design: billing Bx discounts).
// Rules live on products; this page is the cross-cutting view: what deals
// exist, who gets them and what's about to expire. Everything derives
// client-side from the products payload except practice coverage - so unlike
// the other billing tables, this one stays entirely client-paginated: no
// server params, no infinite mode, no mode toggle (a "load more" over
// already-fetched local data would be a fake affordance).

type QuickFilter = 'active' | 'scheduled' | 'ending' | null;

// A type alias, not an interface: only aliases pick up TypeScript's implicit
// index signature, which is what lets this satisfy `DataTableFilterMap`.
type DiscountFilters = {
    product: string[];
    type: string[];
    status: string[];
    quick: QuickFilter;
};

// Matches useViewportPageSize's own default.
const VIEWPORT_DEFAULT_PAGE_SIZE = 20;

export function DiscountsPage() {
    usePageTitle('Discounts');
    const { setTitle, setSubtitle } = usePageHeader();
    const { hasPermission } = useMyPermissions();
    const canEditProducts = hasPermission('products.update');

    // `useDataTable` still holds sort/filter/page state here - it's the same
    // reducer this page used to hand-roll (toggle-sort-with-page-reset,
    // filter-change-resets-page) even though nothing it produces ever reaches
    // a server: `params`/`mode`/`limit` are simply never read below beyond
    // driving the local page size. `mode: 'paged'` at construction keeps
    // `limit` following `setLimit` instead of locking to the infinite chunk
    // size, and it's never changed - there's no `onModeChange` wired to
    // `<DataTable>`, and `mode="paged"` is passed there as a literal, not
    // `table.mode`, so the pager is provably the only footer this page can show.
    const table = useDataTable<DiscountsSortBy, DiscountFilters>({
        defaultSort: { by: 'rule', order: 'asc' },
        defaultLimit: VIEWPORT_DEFAULT_PAGE_SIZE,
        defaultFilters: { product: [], type: [], status: [], quick: null },
        mode: 'paged'
    });
    const { product: productFilter, type: typeFilter, status: statusFilter, quick } = table.filters;

    // Instant, client-side filtering has no debounce to commit through - kept
    // as plain local state rather than `table.search`, with its own page
    // reset on change (every other filter's reset comes free from `setFilter`).
    const [search, setSearch] = useState('');
    const handleSearchChange = (value: string) => {
        setSearch(value);
        table.setPage(1);
    };

    const [editing, setEditing] = useState<{ productId: string; ruleId: string } | null>(null);
    const [showCreate, setShowCreate] = useState(false);

    const { products, isLoading } = useProducts();
    const { practices } = usePracticeOptions();
    const { data: coverage } = useQuery({
        queryKey: ['v2/products', 'discount-coverage'],
        queryFn: fetchDiscountCoverage,
        staleTime: 60_000
    });

    const totalPractices = coverage?.total ?? practices.length;

    const rows = useMemo(() => buildRows(products, Date.now()), [products]);

    const stats = useMemo(
        () => ({
            active: rows.filter((row) => row.status === 'active').length,
            scheduled: rows.filter((row) => row.status === 'scheduled').length,
            endingSoon: rows.filter((row) => row.endingDays !== null).length,
            productsWithRules: new Set(rows.map((row) => row.product.id)).size
        }),
        [rows]
    );

    useEffect(() => {
        setTitle('Discounts');
        setSubtitle(
            `${rows.length} rule${rows.length === 1 ? '' : 's'} across ${stats.productsWithRules} product${stats.productsWithRules === 1 ? '' : 's'}`
        );
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [rows.length, stats.productsWithRules, setTitle, setSubtitle]);

    const productOptions = useMemo(
        () => [...new Set(rows.map((row) => row.product.name))].sort(),
        [rows]
    );

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase();
        return rows.filter((row) => {
            if (quick === 'active' && row.status !== 'active') return false;
            if (quick === 'scheduled' && row.status !== 'scheduled') return false;
            if (quick === 'ending' && row.endingDays === null) return false;
            if (productFilter.length > 0 && !productFilter.includes(row.product.name)) return false;
            if (
                typeFilter.length > 0 &&
                !typeFilter.includes(row.rule.discountType === 'percentage' ? 'Percent' : 'Fixed')
            )
                return false;
            if (statusFilter.length > 0 && !statusFilter.includes(STATUS_LABEL[row.status]))
                return false;
            if (!query) return true;
            return (
                row.rule.name.toLowerCase().includes(query) ||
                row.product.name.toLowerCase().includes(query)
            );
        });
    }, [rows, search, productFilter, typeFilter, statusFilter, quick]);

    const sorted = useMemo(() => {
        const direction = table.order === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => direction * compareRows(a, b, table.sort));
    }, [filtered, table.sort, table.order]);

    const { pageSize, tableCardRef, tableHeaderRef, tableBodyRef, paginationRef, remeasureOnce } =
        useViewportPageSize(VIEWPORT_DEFAULT_PAGE_SIZE);

    const { setLimit } = table;
    useEffect(() => {
        setLimit(pageSize);
    }, [pageSize, setLimit]);

    const totalPages = Math.max(1, Math.ceil(sorted.length / table.limit));
    const currentPage = Math.min(table.page, totalPages);
    const paged = sorted.slice((currentPage - 1) * table.limit, currentPage * table.limit);

    useLayoutEffect(() => {
        if (paged.length > 0) remeasureOnce();
    }, [paged.length, remeasureOnce]);

    const toggleQuick = (filter: Exclude<QuickFilter, null>) => {
        table.setFilter('quick', quick === filter ? null : filter);
    };

    // Re-derive the edited rule from fresh data so saves reflect immediately.
    const editingProduct = editing
        ? (products.find((p) => p.id === editing.productId) ?? null)
        : null;
    const editingRule = editingProduct?.discountRules.find((r) => r.id === editing?.ruleId) ?? null;

    const noRulesAtAll = !isLoading && rows.length === 0;

    const openEditor = (row: RuleRow) =>
        setEditing({ productId: row.product.id, ruleId: row.rule.id });

    const columns = useMemo(
        () =>
            buildDiscountColumns({ totalPractices, canEdit: canEditProducts, onEdit: openEditor }),
        [totalPractices, canEditProducts]
    );

    const filters: DataTableFilter[] = [
        {
            key: 'product',
            label: 'Product',
            multiple: true,
            options: productOptions,
            value: productFilter,
            onChange: (value) => table.setFilter('product', value),
            workQueueVariant: true,
            align: 'right',
            searchable: true
        },
        {
            key: 'type',
            label: 'Type',
            multiple: true,
            options: ['Percent', 'Fixed'],
            value: typeFilter,
            onChange: (value) => table.setFilter('type', value),
            workQueueVariant: true,
            align: 'right',
            searchable: true
        },
        {
            key: 'status',
            label: 'Status',
            multiple: true,
            options: ['Active', 'Scheduled', 'Ended'],
            value: statusFilter,
            onChange: (value) => table.setFilter('status', value),
            workQueueVariant: true,
            align: 'right',
            searchable: true
        }
    ];

    const refs: DataTableRefs = {
        card: tableCardRef,
        head: tableHeaderRef,
        body: tableBodyRef,
        pagination: paginationRef
    };

    const emptyState = noRulesAtAll ? (
        <div className="flex flex-col items-center gap-4">
            <span className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-300">
                <TagIcon className="h-7 w-7" />
            </span>
            <div>
                <p className="text-base font-bold text-gray-800">No discount rules yet</p>
                <p className="text-sm text-gray-400 mt-1">Create them here or from a product.</p>
            </div>
            {canEditProducts && (
                <button
                    onClick={() => setShowCreate(true)}
                    className="bg-black text-white font-semibold text-sm px-4 py-2 rounded-lg hover:bg-gray-900 transition-colors"
                >
                    + New rule
                </button>
            )}
        </div>
    ) : (
        'No rules match the current filters.'
    );

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            {/* Stat cards - same clickable filter cards as the other billing pages. */}
            <div className="flex items-stretch gap-3 px-8 pt-4 shrink-0">
                <StatCard
                    label="Active rules"
                    icon={<CheckCircleIcon className="h-3.5 w-3.5" />}
                    value={String(stats.active)}
                    unit="in window now"
                    isActive={quick === 'active'}
                    onClick={() => toggleQuick('active')}
                />
                <StatCard
                    label="Scheduled"
                    icon={<CalendarDaysIcon className="h-3.5 w-3.5" />}
                    value={String(stats.scheduled)}
                    unit="future start"
                    isActive={quick === 'scheduled'}
                    onClick={() => toggleQuick('scheduled')}
                />
                <StatCard
                    label="Ending soon"
                    icon={<ClockIcon className="h-3.5 w-3.5" />}
                    value={String(stats.endingSoon)}
                    unit={`within ${ENDING_SOON_DAYS} days`}
                    isActive={quick === 'ending'}
                    onClick={() => toggleQuick('ending')}
                />
                <StatCard
                    label="Practices covered"
                    icon={<BuildingStorefrontIcon className="h-3.5 w-3.5" />}
                    value={coverage ? String(coverage.covered) : '…'}
                    unit={`of ${totalPractices} practices`}
                    isActive={false}
                    onClick={() => {}}
                />
            </div>

            <DataTable
                label="Discount rules"
                columns={columns}
                rows={paged}
                getRowKey={(row) => row.rule.id}
                rowAction={canEditProducts ? { onActivate: openEditor, mode: 'click' } : undefined}
                rowClassName={(row) => (row.endingDays !== null ? 'bg-yellow-50/40' : undefined)}
                tableLayout="auto"
                sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                mode="paged"
                pagination={{
                    page: currentPage,
                    totalPages,
                    total: sorted.length,
                    onPage: (p) => table.setPage(Math.min(Math.max(1, p), totalPages))
                }}
                isLoading={isLoading}
                loadingRows={
                    <tr>
                        <td colSpan={7} className="px-3 py-8 text-center">
                            <div className="flex items-center justify-center gap-2 text-gray-400">
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                                <span className="text-sm">Loading rules…</span>
                            </div>
                        </td>
                    </tr>
                }
                emptyState={emptyState}
                refs={refs}
                className="overflow-hidden px-8 pb-8"
                search={{
                    value: search,
                    onChange: handleSearchChange,
                    placeholder: 'Search rule or product name…'
                }}
                filters={filters}
                actions={
                    canEditProducts
                        ? [
                              {
                                  key: 'new-rule',
                                  label: '+ New rule',
                                  variant: 'primary',
                                  onClick: () => setShowCreate(true)
                              }
                          ]
                        : undefined
                }
            />

            {editingProduct && editingRule && (
                <DiscountRuleModal
                    product={editingProduct}
                    rule={editingRule}
                    onClose={() => setEditing(null)}
                />
            )}

            {showCreate && (
                <DiscountRuleModal
                    product={null}
                    products={products}
                    rule={null}
                    onClose={() => setShowCreate(false)}
                />
            )}
        </div>
    );
}
